import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.trippang.com";

type FontSet = {
  title_pc: number;
  title_mobile: number;
  subtitle_pc: number;
  subtitle_mobile: number;
};
type Group = "VIP" | "PLATINUM" | "GOLD" | "SILVER";
type FontGroups = Record<Group, FontSet>;

const DEFAULTS: FontGroups = {
  VIP:      { title_pc: 20, title_mobile: 16, subtitle_pc: 14, subtitle_mobile: 11 },
  PLATINUM: { title_pc: 18, title_mobile: 14, subtitle_pc: 13, subtitle_mobile: 10 },
  GOLD:     { title_pc: 16, title_mobile: 12, subtitle_pc: 12, subtitle_mobile:  9 },
  SILVER:   { title_pc: 14, title_mobile: 10, subtitle_pc: 10, subtitle_mobile:  8 },
};

// 배너 등급(banner_grade) → 그룹 매핑. 1=VIP, 2=PLATINUM, 3=GOLD, 4·5·6=SILVER
export function gradeToGroup(grade: number | null | undefined): Group {
  if (grade === 1) return "VIP";
  if (grade === 2) return "PLATINUM";
  if (grade === 3) return "GOLD";
  return "SILVER";
}

// 프론트 tier string → 그룹 매핑 (Banner.tier 사용처용)
export function tierToGroup(tier?: string | null): Group {
  switch ((tier || "").toLowerCase()) {
    case "vip": return "VIP";
    case "platinum": return "PLATINUM";
    case "gold": return "GOLD";
    default: return "SILVER"; // silver/standard/event 모두 SILVER 폰트
  }
}

let cached: FontGroups | null = null;
let fetchPromise: Promise<FontGroups> | null = null;
const listeners = new Set<(g: FontGroups) => void>();

function fetchOnce(): Promise<FontGroups> {
  if (cached) return Promise.resolve(cached);
  if (!fetchPromise) {
    fetchPromise = fetch(`${API_URL}/public/business/banner-fonts`)
      .then((r) => (r.ok ? r.json() : DEFAULTS))
      .then((d: Partial<FontGroups> & FontSet) => {
        const merge = (g: Group): FontSet => {
          const src = (d as FontGroups)[g];
          if (src && typeof src.title_pc === "number") return src;
          // 레거시 응답(top-level title_pc/...) 인 경우 모든 그룹에 동일 적용
          if (typeof d.title_pc === "number") {
            return {
              title_pc: d.title_pc, title_mobile: d.title_mobile,
              subtitle_pc: d.subtitle_pc, subtitle_mobile: d.subtitle_mobile,
            };
          }
          return DEFAULTS[g];
        };
        cached = { VIP: merge("VIP"), PLATINUM: merge("PLATINUM"), GOLD: merge("GOLD"), SILVER: merge("SILVER") };
        listeners.forEach((l) => l(cached!));
        return cached;
      })
      .catch(() => DEFAULTS);
  }
  return fetchPromise;
}

/**
 * useBannerFonts — 배너 등급 그룹별 폰트 정책 + viewport (PC/모바일) 분기.
 *  - 인자 grade(또는 banner_grade) 전달 시 해당 그룹 폰트 반환
 *  - 인자 없으면 VIP 기본 (레거시 호환)
 *  - 컴포넌트가 직접 inline style 로 사용
 */
export function useBannerFonts(gradeOrTier?: number | string | null) {
  const [groups, setGroups] = useState<FontGroups>(cached ?? DEFAULTS);
  const [isPc, setIsPc] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : true
  );

  useEffect(() => {
    let mounted = true;
    if (cached) {
      setGroups(cached);
    } else {
      fetchOnce().then((g) => { if (mounted) setGroups(g); });
    }
    const listener = (g: FontGroups) => { if (mounted) setGroups(g); };
    listeners.add(listener);
    return () => {
      mounted = false;
      listeners.delete(listener);
    };
  }, []);

  useEffect(() => {
    const m = window.matchMedia("(min-width: 768px)");
    const handler = () => setIsPc(m.matches);
    if (m.addEventListener) m.addEventListener("change", handler);
    else (m as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(handler);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", handler);
      else (m as MediaQueryList & { removeListener: (cb: () => void) => void }).removeListener(handler);
    };
  }, []);

  const grp: Group = typeof gradeOrTier === "string"
    ? tierToGroup(gradeOrTier)
    : gradeToGroup(gradeOrTier);
  const f = groups[grp];
  return {
    titleSize: `${isPc ? f.title_pc : f.title_mobile}px`,
    subtitleSize: `${isPc ? f.subtitle_pc : f.subtitle_mobile}px`,
  };
}

/**
 * useBannerFontGroups — 그룹 dict + isPc 통째 반환 (반복 안 hook 호출 회피).
 * 사용 예: const { groups, isPc } = useBannerFontGroups();
 *         banners.map(b => { const s = getBannerSize(b.banner_grade, isPc, groups); ... })
 */
export function useBannerFontGroups(): { groups: FontGroups; isPc: boolean } {
  const [groups, setGroups] = useState<FontGroups>(cached ?? DEFAULTS);
  const [isPc, setIsPc] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 768px)").matches
      : true
  );
  useEffect(() => {
    let mounted = true;
    if (cached) setGroups(cached);
    else fetchOnce().then((g) => { if (mounted) setGroups(g); });
    const listener = (g: FontGroups) => { if (mounted) setGroups(g); };
    listeners.add(listener);
    return () => { mounted = false; listeners.delete(listener); };
  }, []);
  useEffect(() => {
    const m = window.matchMedia("(min-width: 768px)");
    const handler = () => setIsPc(m.matches);
    if (m.addEventListener) m.addEventListener("change", handler);
    else (m as MediaQueryList & { addListener: (cb: () => void) => void }).addListener(handler);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", handler);
      else (m as MediaQueryList & { removeListener: (cb: () => void) => void }).removeListener(handler);
    };
  }, []);
  return { groups, isPc };
}

export function getBannerSize(
  gradeOrTier: number | string | null | undefined,
  isPc: boolean,
  groups: FontGroups,
): { titleSize: string; subtitleSize: string } {
  const g: Group = typeof gradeOrTier === "string" ? tierToGroup(gradeOrTier) : gradeToGroup(gradeOrTier);
  const f = groups[g];
  return {
    titleSize: `${isPc ? f.title_pc : f.title_mobile}px`,
    subtitleSize: `${isPc ? f.subtitle_pc : f.subtitle_mobile}px`,
  };
}
